import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrivateRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * PrivateRoute component that requires authentication
 * 
 * Redirects to login page if user is not authenticated
 * 
 * @param {React.ReactNode} children - The components to render when authenticated
 * @param {string} redirectTo - Path to redirect to if not authenticated (defaults to /login)
 * @returns React component
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ 
  children, 
  redirectTo = '/login'
}) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    console.log('[PrivateRoute] 🔐 Checking authentication for path:', location.pathname);
    
    // Ensure auth is initialized
    if (!auth) {
      console.error('[PrivateRoute] ❌ Firebase Auth not initialized!');
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }
    
    // Check Firebase auth state
    // onAuthStateChanged fires immediately with current state, then on changes
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      const authenticated = !!user;
      console.log('[PrivateRoute] 🔑 Auth state changed:', { 
        authenticated, 
        hasUser: !!user, 
        userId: user?.uid?.substring(0, 8),
        email: user?.email 
      });
      
      setIsAuthenticated(authenticated);
      setLoading(false);
      
      // If not authenticated, log for debugging
      if (!authenticated) {
        console.log('[PrivateRoute] ❌ User not authenticated, will redirect to:', redirectTo);
      } else {
        console.log('[PrivateRoute] ✅ User authenticated, allowing access');
      }
    }, (error) => {
      console.error('[PrivateRoute] ❌ Auth state error:', error);
      setIsAuthenticated(false);
      setLoading(false);
    });
    
    // Return cleanup function
    return () => {
      console.log('[PrivateRoute] 🧹 Cleaning up auth listener');
      unsubscribe();
    };
  }, [location.pathname, redirectTo]);
  
  // Show loading state while checking auth
  if (loading || isAuthenticated === null) {
    console.log('[PrivateRoute] ⏳ Showing loading state');
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }
  
  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    console.log('[PrivateRoute] 🚫 Not authenticated, redirecting to:', redirectTo);
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }
  
  // Render children if authenticated
  console.log('[PrivateRoute] ✅ Authenticated, rendering children');
  return <>{children}</>;
};

export default PrivateRoute; 